import React from 'react';
import dayjs from 'dayjs';
import { RRule } from 'rrule';

export interface IBlockedDateEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  allDay: boolean;
  rrule: string;
  block_reason: string;
  employees: IBlockedDateEmployees[] | null;
  recurring?: string;
  updated_at?: string;
}

export interface IBlockedDateEmployees {
  employee_id: number;
  employee_name: string;
  mobile_number: string;
  employee_id_number?: string;
  employee_number?: string;
}

export interface IOdpBlockDate {
  id: string;
  title: string;
  rrule: string;
  block_reason: string;
  updated_at?: string;
}

type IGroupedDateEmployeeResponse = {
  id: string;
  title: string;
  rrule: string;
  block_reason: string;
  employee_id: number;
  employee_name: string;
  employee_id_number?: string;
  employee_number?: string;
  updated_at?: string;
};

type IEmployeeGroupedDates = {
  [key: string]: IGroupedDateEmployeeResponse[];
};
type IEmployerOdpGroupedDate = {
  [key: string]: IOdpBlockDate;
};

export type IBlockedDatesResponse = {
  employees: IEmployeeGroupedDates;
  employer: IEmployerOdpGroupedDate;
};

interface IGroupedBlockedDateEvents {
  pastEvents: IBlockedDateEvent[];
  futureEvents: IBlockedDateEvent[];
  currentEvents: IBlockedDateEvent[];
}
export type IGroupCategory = 'past' | 'current' | 'future';

const categorizeEvent = (
  event: IBlockedDateEvent,
  startOfToday: dayjs.Dayjs,
  endOfToday: dayjs.Dayjs
): IGroupCategory => {
  const eventStart = dayjs(event.start);
  const eventEnd = dayjs(event.end);

  // Helper function for non-recurring events
  const categorizeNonRecurring = (): IGroupCategory => {
    if (eventEnd.isBefore(startOfToday)) return 'past';
    if (
      eventStart.isSame(startOfToday, 'day') ||
      eventEnd.isSame(endOfToday, 'day') ||
      (eventStart.isBefore(startOfToday) && eventEnd.isAfter(endOfToday))
    )
      return 'current';
    return 'future';
  };

  // If no rrule, handle as non-recurring event
  if (!event.rrule) return categorizeNonRecurring();

  // Handle recurring events
  const rrule = RRule.fromString(event.rrule);
  const todayOccurrences = rrule.between(
    startOfToday.toDate(),
    endOfToday.toDate(),
    true
  );

  // If there are occurrences today, it's current
  if (todayOccurrences.length > 0) return 'current';

  // Check future occurrences
  const nextOccurrence = rrule.after(startOfToday.toDate(), true);
  return nextOccurrence ? 'future' : 'past';
};

export const getSplitBlockedDates = (
  events: IBlockedDateEvent[]
): IGroupedBlockedDateEvents => {
  const pastEvents: IBlockedDateEvent[] = [];
  const currentEvents: IBlockedDateEvent[] = [];
  const futureEvents: IBlockedDateEvent[] = [];

  const now = dayjs();
  const startOfToday = now.startOf('day');
  const endOfToday = now.endOf('day');

  events.forEach((event) => {
    const category = categorizeEvent(event, startOfToday, endOfToday);
    switch (category) {
      case 'past': {
        pastEvents.push(event);
        break;
      }
      case 'current': {
        currentEvents.push(event);
        break;
      }
      case 'future': {
        futureEvents.push(event);
        break;
      }
      default: {
        break;
      }
    }
  });

  return { pastEvents, currentEvents, futureEvents };
};

// Helper function to create a blocked date event
const createBlockedDateEvent = (
  blockedDateInstance: IOdpBlockDate,
  startDate: Date,
  endDate: Date,
  employees: IBlockedDateEmployees[] | null,
  recurringText: string
): IBlockedDateEvent => ({
  id: blockedDateInstance.id,
  title: blockedDateInstance.title,
  start: dayjs(startDate).startOf('day').toDate(),
  end: dayjs(endDate).endOf('day').toDate(),
  allDay: true,
  rrule: blockedDateInstance.rrule,
  block_reason: blockedDateInstance.block_reason,
  employees,
  recurring: recurringText,
  updated_at: blockedDateInstance.updated_at,
});

export const getBlockedDateEvent = (
  blockedDate: IOdpBlockDate,
  employees: IBlockedDateEmployees[] | null = null
): IBlockedDateEvent[] => {
  const EVENTS = [];
  const blockedDateInstance = blockedDate;

  if (blockedDate.rrule.indexOf('BYDAY=') > 0) {
    /*
        UPDATE STRING to accomodate https://www.npmjs.com/package/rrule/v/2.7.2#differences-from-icalendar-rfc
        RRule has no byday keyword. The equivalent keyword has been replaced by the byweekday keyword, 
        to remove the ambiguity present in the original keyword.
        */
    blockedDateInstance.rrule = blockedDate.rrule.replace(
      'BYDAY=',
      'BYWEEKDAY='
    );
  }

  const rrule = RRule.fromString(blockedDateInstance.rrule);
  const frequency = rrule.options.freq;
  const allRecurrenceInstances = rrule.all();

  let recurringText = '';
  switch (frequency) {
    case RRule.DAILY: {
      recurringText = `Recurring daily`;
      break;
    }
    case RRule.WEEKLY: {
      const daysOfWeek = rrule.options.byweekday
        ?.map((weekday) => dayjs().day(weekday).format('dddd'))
        .join(', ');
      recurringText = `Recurring weekly on day(s) ${daysOfWeek}`;
      break;
    }
    case RRule.MONTHLY: {
      const daysOfMonth = rrule.options.bymonthday?.join(', ');
      recurringText = `Recurring monthly on day(s) ${daysOfMonth}`;
      break;
    }
    case RRule.YEARLY: {
      const daysOfYear = rrule.options.byyearday?.join(', ');
      recurringText = `Recurring yearly on days ${daysOfYear}`;
      break;
    }
    default: {
      recurringText = '';
      break;
    }
  }

  if (frequency === RRule.DAILY) {
    const startDate = rrule.options.dtstart;
    const endDate = rrule.options.until || allRecurrenceInstances[allRecurrenceInstances.length - 1];

    const event = createBlockedDateEvent(
      blockedDateInstance,
      startDate,
      endDate,
      employees,
      recurringText
    );
    EVENTS.push(event);
  } else if (
    frequency === RRule.YEARLY ||
    frequency === RRule.MONTHLY ||
    frequency === RRule.WEEKLY
  ) {
    const startDate = allRecurrenceInstances[0];
    const endDate =
      rrule.options.until ||
      allRecurrenceInstances[allRecurrenceInstances.length - 1];

    const event = createBlockedDateEvent(
      blockedDateInstance,
      startDate,
      endDate,
      employees,
      recurringText
    );
    EVENTS.push(event);
  }

  return EVENTS;
};

// Helper function to create employee object from blocked date response
const createEmployeeFromBlockedDate = (
  blockedDate: any
): IBlockedDateEmployees => ({
  employee_id: blockedDate.employee_id,
  employee_name: blockedDate.employee_name,
  employee_id_number: blockedDate.employee_id_number,
  employee_number: blockedDate.employee_number,
  mobile_number: blockedDate.mobile_number,
});

// Helper function to create IOdpBlockDate from response
const createOdpBlockDate = (blockedDate: any): IOdpBlockDate => ({
  id: blockedDate.id,
  title: blockedDate.title,
  rrule: blockedDate.rrule,
  block_reason: blockedDate.block_reason,
  updated_at: blockedDate.updated_at,
});

export const getAllBlockedDateEvents = (
  odpLimitsBlockedDates: IBlockedDatesResponse,
  employeeId: string
): IGroupedBlockedDateEvents => {
  const employeeEvents: IBlockedDateEvent[] = [];
  const employerEvents: IBlockedDateEvent[] = [];

  const { employer: employerBlockedDates, employees: employeeBlockedDates } =
    odpLimitsBlockedDates;

  if (employerBlockedDates) {
    Object.keys(employerBlockedDates).forEach((entry) => {
      employerEvents.push(...getBlockedDateEvent(employerBlockedDates[entry]));
    });
  }

  if (employeeBlockedDates) {
    Object.keys(employeeBlockedDates).forEach((entry) => {
      const employees: IBlockedDateEmployees[] = [];
      let employeeBlockedDate: IOdpBlockDate = {
        id: '',
        title: '',
        rrule: '',
        block_reason: '',
      };

      employeeBlockedDates[entry].forEach((blockedDate: any) => {
        employeeBlockedDate = createOdpBlockDate(blockedDate);
        employees.push(createEmployeeFromBlockedDate(blockedDate));
      });

      if (
        employees.some(
          (employee) => employee.employee_id === parseInt(employeeId, 10)
        )
      ) {
        employeeEvents.push(
          ...getBlockedDateEvent(employeeBlockedDate, employees)
        );
      }
    });
  }
  const blockdates = Array.from(
    new Map(
      [...employerEvents, ...employeeEvents].map((event) => [event.id, event])
    ).values()
  );
  const groupedBlockedDates = getSplitBlockedDates(blockdates);
  return groupedBlockedDates;
};

const EmployeeBlockDates: React.FC<{
  groupedBlockDates: IGroupedBlockedDateEvents;
}> = ({
  groupedBlockDates,
}: {
  groupedBlockDates: IGroupedBlockedDateEvents;
}) => {
  const getColor = (type: IGroupCategory) => {
    switch (type) {
      case 'past':
        return '#9ca6ae';
      case 'current':
        return '#ED7687';
      default:
        return 'rgba(6, 33, 51, 1)';
    }
  };

  const formatEventDate = (start: Date, end: Date) =>
    `${dayjs(start).format('DD MMM YYYY')} - ${dayjs(end).format('DD MMM YYYY')}`;

  const renderEventCard = (event: IBlockedDateEvent, type: IGroupCategory) => (
    <div
      className="card"
      key={event.id}
      style={{
        borderLeft: `3px solid ${getColor(type)}`,
        marginBottom: '4px',
      }}>
      <div className="card-body">
        <h3>
          {event.title} -{' '}
          {event.employees
            ? `Individual Block (${event.employees.length})`
            : 'Company Block'}
        </h3>
        <small>
          {formatEventDate(event.start, event.end)}
          {event.recurring ? ` (${event.recurring})` : ''}
        </small>
        {event.updated_at && (
          <div>
            <small className="text-muted">
              Last updated: {dayjs(event.updated_at).format('DD MMM YYYY, hh:mm A')}
            </small>
          </div>
        )}
      </div>
    </div>
  );

  const renderEventSection = (
    title: string,
    events: IBlockedDateEvent[],
    type: IGroupCategory
  ) => {
    if (events?.length === 0) return null;

    return (
      <div>
        <h2>{title}</h2>
        {events.map((event) => renderEventCard(event, type))}
      </div>
    );
  };
  return (
    <div className="container">
      <br />
      <h1>Employee&apos;s Blocked Dates</h1>
      <br />
      {renderEventSection(
        "Today's active blocked dates",
        groupedBlockDates.currentEvents,
        'current'
      )}
      <br />
      {renderEventSection(
        'Future blocked dates',
        groupedBlockDates.futureEvents,
        'future'
      )}
      <br />
      {renderEventSection(
        'Past block dates (last 30 days only)',
        groupedBlockDates.pastEvents,
        'past'
      )}
    </div>
  );
};

export default EmployeeBlockDates;
